import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner } from './core';

export function BannersTab({ theme }) {
  const light = theme === 'light';
  const [config, setConfig] = useState({ hero_items: [], highlight_items: [], highlight_scroll_duration: 8, mobile_profile_banners: [], mobile_profile_duration: 3, mobile_profile_transition: 1 });
  const [allContents, setAllContents] = useState([]);
  const [heroSearch, setHeroSearch] = useState('');
  const [hlSearch, setHlSearch] = useState('');
  const [mobileSearch, setMobileSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-800' : 'bg-white/5 border border-white/10 text-white';
  const hintCls = light ? 'text-gray-400' : 'text-white/30';
  const cardCls = light ? 'bg-gray-50 border border-gray-100' : 'bg-white/5 border border-white/5';

  useEffect(() => {
    const load = async () => {
      try {
        const [cfgRes, cRes] = await Promise.all([
          axios.get(`${API}/admin/banner-config`, { withCredentials: true }),
          axios.get(`${API}/contents`, { withCredentials: true }),
        ]);
        setConfig({
          hero_items: cfgRes.data.hero_items || cfgRes.data.items || [],
          highlight_items: cfgRes.data.highlight_items || [],
          highlight_scroll_duration: cfgRes.data.highlight_scroll_duration || cfgRes.data.scroll_duration || 8,
          mobile_profile_banners: cfgRes.data.mobile_profile_banners || [],
          mobile_profile_duration: cfgRes.data.mobile_profile_duration || 3,
          mobile_profile_transition: cfgRes.data.mobile_profile_transition || 1,
        });
        setAllContents(cRes.data.filter(c => c.type !== 'game'));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/admin/banner-config`, config, { withCredentials: true }); } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const addTo = (listKey, content) => {
    const list = config[listKey] || [];
    if (list.find(i => i.content_id === content.id)) return;
    if (listKey === 'highlight_items' && list.length >= 4) return;
    if (listKey === 'mobile_profile_banners' && list.length >= 5) return;
    setConfig(prev => ({ ...prev, [listKey]: [...list, { content_id: content.id, title: content.title, thumbnail_url: content.thumbnail_url || content.banner_url }] }));
    if (listKey === 'hero_items') setHeroSearch('');
    else if (listKey === 'mobile_profile_banners') setMobileSearch('');
    else setHlSearch('');
  };

  const removeFrom = (listKey, contentId) => {
    setConfig(prev => ({ ...prev, [listKey]: (prev[listKey] || []).filter(i => i.content_id !== contentId) }));
  };

  const moveIn = (listKey, idx, dir) => {
    const items = [...(config[listKey] || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setConfig(prev => ({ ...prev, [listKey]: items }));
  };

  const filterContents = (search, listKey) => search.length >= 2
    ? allContents.filter(c => c.title.toLowerCase().includes(search.toLowerCase()) && !(config[listKey] || []).find(i => i.content_id === c.id))
    : [];

  const renderItemList = (listKey, items) => {
    const isMobile = listKey === 'mobile_profile_banners';
    return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const content = allContents.find(c => c.id === item.content_id);
        const mobileUrl = content?.mobile_banner_url;
        return (
          <div key={item.content_id} data-testid={`${listKey}-item-${idx}`}
            className={`flex items-center gap-3 p-3 rounded-xl ${cardCls}`}>
            <span className={`text-lg font-bold w-6 text-center ${light ? 'text-gray-300' : 'text-white/20'}`}>{idx + 1}</span>
            {isMobile ? (
              mobileUrl
                ? <img src={mobileUrl} alt="" className="w-12 h-[72px] object-cover rounded flex-none" data-testid={`mobile-banner-thumb-${idx}`} />
                : <div className={`w-12 h-[72px] rounded flex-none flex items-center justify-center border border-dashed ${light ? 'bg-gray-50 border-gray-300' : 'bg-white/5 border-white/15'}`} data-testid={`mobile-banner-missing-${idx}`}>
                    <span className={`text-[8px] text-center leading-tight px-0.5 ${light ? 'text-gray-400' : 'text-white/30'}`}>Pas de 2:3</span>
                  </div>
            ) : (
              <img src={item.thumbnail_url || content?.banner_url} alt="" className="w-20 h-12 object-cover rounded flex-none" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>{item.title || content?.title}</p>
              <p className={`text-[10px] ${hintCls}`}>{TYPE_LABELS[content?.type] || 'Fiction'}{content?.trailer_url ? ' · Bande-annonce' : ''}</p>
              {isMobile && !mobileUrl && <p className="text-[9px] text-amber-500 font-medium">⚠ Aucune bannière mobile portrait — le banner 16:9 sera utilisé en fallback</p>}
              {isMobile && mobileUrl && <p className="text-[9px] text-green-500 font-medium">Bannière mobile portrait 2:3 ✓</p>}
            </div>
            <div className="flex items-center gap-1 flex-none">
              <button onClick={() => moveIn(listKey, idx, -1)} disabled={idx === 0}
                className={`p-1 rounded transition-colors disabled:opacity-20 ${light ? 'text-gray-400 hover:text-gray-700' : 'text-white/30 hover:text-white'}`}><ChevronUp size={14} /></button>
              <button onClick={() => moveIn(listKey, idx, 1)} disabled={idx === items.length - 1}
                className={`p-1 rounded transition-colors disabled:opacity-20 ${light ? 'text-gray-400 hover:text-gray-700' : 'text-white/30 hover:text-white'}`}><ChevronDown size={14} /></button>
              <button onClick={() => removeFrom(listKey, item.content_id)}
                className={`p-1 rounded transition-colors ${light ? 'text-gray-300 hover:text-red-500' : 'text-white/20 hover:text-red-400'}`}><Trash2 size={14} /></button>
            </div>
          </div>
        );
      })}
    </div>
    );
  };

  const renderSearch = (search, setSearchFn, listKey) => {
    const results = filterContents(search, listKey);
    return (
      <div className={`p-4 rounded-xl ${cardCls}`}>
        <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Ajouter un contenu</label>
        <input value={search} onChange={e => setSearchFn(e.target.value)} placeholder="Rechercher un film ou une série..."
          className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
        {results.length > 0 && (
          <div className={`mt-2 max-h-48 overflow-y-auto rounded-lg border ${light ? 'bg-white border-gray-200' : 'bg-[#0d0520] border-white/10'}`}>
            {results.slice(0, 8).map(c => (
              <button key={c.id} onClick={() => addTo(listKey, c)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${light ? 'hover:bg-gray-50' : 'hover:bg-white/5'}`}>
                <img src={c.thumbnail_url || c.banner_url} alt="" className="w-14 h-8 object-cover rounded flex-none" />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${light ? 'text-gray-800' : 'text-white'}`}>{c.title}</p>
                  <p className={`text-[10px] ${hintCls}`}>{TYPE_LABELS[c.type] || 'Fiction'} · {c.year}</p>
                </div>
                {c.trailer_url && <span className="text-[9px] text-green-400 font-semibold">BA</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <LoadingSpinner theme={theme} />;

  const heroItems = config.hero_items || [];
  const hlItems = config.highlight_items || [];

  return (
    <div className="space-y-8" data-testid="banners-tab">
      <div className="flex items-center justify-between">
        <h3 className={`text-sm font-bold ${light ? 'text-gray-800' : 'text-white'}`}>Gestion des Bannières</h3>
        <button onClick={save} disabled={saving} data-testid="save-banner-config"
          className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>

      {/* ── HERO BANNERS ── */}
      <div className={`p-5 rounded-2xl ${light ? 'bg-white border border-gray-100 shadow-sm' : 'bg-white/[0.02] border border-white/5'}`}>
        <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${light ? 'text-gray-800' : 'text-white'}`}>Bannières Hero</h4>
        <p className={`text-[10px] mb-4 ${hintCls}`}>Bande-annonces en haut de la page. Défilement automatique à la fin de chaque bande-annonce.</p>
        {renderSearch(heroSearch, setHeroSearch, 'hero_items')}
        <div className="mt-4">
          <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Ordre ({heroItems.length})</label>
          <p className={`text-[10px] mb-2 ${hintCls}`}>{heroItems.length === 0 ? 'Aucune — films avec bande-annonce affichés aléatoirement.' : ''}</p>
          {renderItemList('hero_items', heroItems)}
        </div>
      </div>

      {/* ── HIGHLIGHTS ── */}
      <div className={`p-5 rounded-2xl ${light ? 'bg-white border border-gray-100 shadow-sm' : 'bg-white/[0.02] border border-white/5'}`}>
        <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${light ? 'text-gray-800' : 'text-white'}`}>Highlights</h4>
        <p className={`text-[10px] mb-4 ${hintCls}`}>Jusqu'à 4 bannières mises en avant entre les lignes de contenu.</p>

        {/* Scroll duration — highlights only */}
        <div className={`p-3 rounded-xl mb-4 ${cardCls}`}>
          <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Durée de défilement (secondes)</label>
          <input type="number" min={3} max={30} value={config.highlight_scroll_duration || 8}
            onChange={e => setConfig(prev => ({ ...prev, highlight_scroll_duration: parseInt(e.target.value) || 8 }))}
            className={`mt-1 w-24 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`}
            data-testid="highlight-duration-input" />
          <p className={`text-[10px] mt-1 ${hintCls}`}>Temps d'affichage de chaque highlight (3-30s)</p>
        </div>

        {renderSearch(hlSearch, setHlSearch, 'highlight_items')}
        <div className="mt-4">
          <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Highlights ({hlItems.length}/4)</label>
          <p className={`text-[10px] mb-2 ${hintCls}`}>{hlItems.length === 0 ? 'Aucun — contenus les plus vus sélectionnés automatiquement.' : ''}</p>
          {renderItemList('highlight_items', hlItems)}
        </div>
      </div>

      {/* ── MOBILE — PAGE PROFIL ── */}
      <div className={`p-5 rounded-2xl ${light ? 'bg-white border border-gray-100 shadow-sm' : 'bg-white/[0.02] border border-white/5'}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base">📱</span>
          <h4 className={`text-xs font-bold uppercase tracking-wider ${light ? 'text-gray-800' : 'text-white'}`}>Mobile — Page de sélection de profil</h4>
        </div>
        <p className={`text-[10px] mb-4 ${hintCls}`}>Jusqu'à 5 vignettes qui défilent en arrière-plan de la page de sélection de profil sur l'app mobile. Fondu enchaîné entre chaque image.</p>

        <div className={`p-3 rounded-xl mb-4 ${cardCls}`}>
          <p className={`text-[10px] font-semibold ${light ? 'text-[#E9641A]' : 'text-[#E9641A]'}`}>Format requis : Portrait 2:3 — 1080 x 1620 px (JPG/WebP, &lt; 500 Ko)</p>
          <p className={`text-[10px] mt-1 ${hintCls}`}>Uploadez une "Bannière mobile (portrait)" dans la fiche de chaque contenu (onglet Contenus &gt; Modifier). Le champ <strong>mobile_banner_url</strong> sera utilisé automatiquement. Si absent, le banner horizontal sera utilisé en fallback.</p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className={`p-3 rounded-xl ${cardCls}`}>
            <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Durée d'affichage (sec)</label>
            <input type="number" min={2} max={10} value={config.mobile_profile_duration || 3}
              onChange={e => setConfig(prev => ({ ...prev, mobile_profile_duration: parseInt(e.target.value) || 3 }))}
              className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`}
              data-testid="mobile-duration-input" />
            <p className={`text-[10px] mt-1 ${hintCls}`}>Temps par vignette (2-10s)</p>
          </div>
          <div className={`p-3 rounded-xl ${cardCls}`}>
            <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Transition fondu (sec)</label>
            <input type="number" min={0.5} max={3} step={0.5} value={config.mobile_profile_transition || 1}
              onChange={e => setConfig(prev => ({ ...prev, mobile_profile_transition: parseFloat(e.target.value) || 1 }))}
              className={`mt-1 w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`}
              data-testid="mobile-transition-input" />
            <p className={`text-[10px] mt-1 ${hintCls}`}>Durée du fondu enchaîné (0.5-3s)</p>
          </div>
        </div>

        {renderSearch(mobileSearch, setMobileSearch, 'mobile_profile_banners')}
        <div className="mt-4">
          <label className={`text-xs font-semibold ${light ? 'text-gray-600' : 'text-white/60'}`}>Vignettes mobile ({(config.mobile_profile_banners || []).length}/5)</label>
          <p className={`text-[10px] mb-2 ${hintCls}`}>{(config.mobile_profile_banners || []).length < 5 ? 'Complétées automatiquement à 5 avec un mix de tous les types (films, séries, documentaires, jeunesse).' : ''}</p>
          {renderItemList('mobile_profile_banners', config.mobile_profile_banners || [])}
        </div>
      </div>
    </div>
  );
}

